// Shared types for taxonomy tree components.

import type { TaxonomyNode } from '@/lib/api/frameworkConfig'

/** A tree node enriched with children for rendering. */
export interface TreeNode {
  id: string
  parentId: string | null
  path: string
  depth: number
  name: string
  description?: string
  children: TreeNode[]
}

/** Build a nested tree structure from the flat API response. */
export function buildTree(nodes: TaxonomyNode[]): TreeNode[] {
  const nodeMap = new Map<string, TreeNode>()
  const roots: TreeNode[] = []

  // First pass: create TreeNode wrappers
  for (const n of nodes) {
    nodeMap.set(n.id, {
      id: n.id,
      parentId: n.parent_id,
      path: n.path,
      depth: n.depth,
      name: n.name,
      description: n.description,
      children: [],
    })
  }

  // Second pass: wire children to parents
  for (const n of nodes) {
    const treeNode = nodeMap.get(n.id)!
    if (n.parent_id) {
      const parent = nodeMap.get(n.parent_id)
      if (parent) {
        parent.children.push(treeNode)
      } else {
        roots.push(treeNode)
      }
    } else {
      roots.push(treeNode)
    }
  }

  return roots
}

/** Flatten a tree back to TaxonomyNodeInput[] for API submission.
 *  Walks depth-first so parents always appear before children (API contract). */
export interface FlatNodeInput {
  parentPath: string
  name: string
  description: string
}

export function flattenTree(roots: TreeNode[]): FlatNodeInput[] {
  const result: FlatNodeInput[] = []

  // We need to re-derive paths from tree structure since the user may have
  // added/removed nodes. Walk depth-first, tracking assigned paths.
  function walk(nodes: TreeNode[], parentPath: string) {
    for (let i = 0; i < nodes.length; i++) {
      const node = nodes[i]!
      const segment = String(i + 1).padStart(3, '0')
      const path = parentPath ? `${parentPath}.${segment}` : segment
      result.push({
        parentPath: parentPath,
        name: node.name,
        description: node.description ?? '',
      })
      walk(node.children, path)
    }
  }

  walk(roots, '')
  return result
}

/** Derive a display path segment for a node based on its position among siblings. */
export function derivePath(node: TreeNode, siblings: TreeNode[]): string {
  const index = siblings.indexOf(node)
  return String(index + 1).padStart(3, '0')
}

/** Count all nodes in the tree (roots + all descendants). */
export function countNodes(roots: TreeNode[]): number {
  let count = 0
  function walk(nodes: TreeNode[]) {
    for (const n of nodes) {
      count++
      walk(n.children)
    }
  }
  walk(roots)
  return count
}
